'use client'

import { motion } from 'framer-motion'
import { FlickeringGrid } from '@/components/magicui/flickering-grid'
import Safari from '@/components/ui/safari'
import Section from '@/components/ui/section'
import { config } from '@/config'
import { cn } from '@/utils/cn'

const features = [
  {
    title: 'Visualize Prospect Domain Strength',
    description:
      'Only target top backlink prospects with DR, backlink data, referring domains, keywords, and traffic estimates.',
    className: 'hover:bg-red-500/10 transition-all duration-500 ease-out',
    content: (
      <>
        <Safari
          src="/landing/solution/1-light.png"
          url={config.site.url}
          className={cn(
            '-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]',
            'dark:hidden',
          )}
        />
        <Safari
          src="/landing/solution/1-dark.png"
          url={config.site.url}
          className={cn(
            '-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]',
            'hidden dark:block',
          )}
        />
      </>
    ),
  },
  {
    title: 'Pinpoint High-Value Prospect URLs',
    description:
      'Pinpoint prime URLs. Analyze DR, content type, commercial intent & quality scores for relevant, impactful outreach.',
    className: 'order-3 xl:order-none hover:bg-blue-500/10 transition-all duration-500 ease-out',
    content: (
      <>
        <Safari
          src="/landing/solution/2-light.png"
          url={config.site.url}
          className={cn(
            '-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]',
            'dark:hidden',
          )}
        />
        <Safari
          src="/landing/solution/2-dark.png"
          url={config.site.url}
          className={cn(
            '-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]',
            'hidden dark:block',
          )}
        />
      </>
    ),
  },
  {
    title: 'Monitor Your Acquired Backlinks',
    description:
      'Track your live backlinks. View source/target URLs, Inlink Rank, nofollow status, and discovery dates to monitor your profile.',
    className: 'md:row-span-2 hover:bg-orange-500/10 transition-all duration-500 ease-out',
    content: (
      <>
        <FlickeringGrid
          className="absolute inset-0 z-0 [mask:radial-gradient(circle_at_center,#fff_400px,transparent_0)]"
          squareSize={4}
          gridGap={6}
          color="#000"
          maxOpacity={0.1}
          flickerChance={0.1}
          height={800}
          width={800}
        />
        <Safari
          src="/landing/solution/3-light.png"
          url={config.site.url}
          className={cn(
            '-mb-48 mt-16 ml-12 h-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-x-[-10px]',
            'dark:hidden',
          )}
        />
        <Safari
          src="/landing/solution/3-dark.png"
          url={config.site.url}
          className={cn(
            '-mb-48 mt-16 ml-12 h-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-x-[-10px]',
            'hidden dark:block',
          )}
        />
      </>
    ),
  },
  {
    title: 'Convert Backlink Opportunities',
    description:
      'Track identified opportunities. Assess viability, relevance & suggested exchanges to convert them into valuable backlinks.',
    className:
      'order-4 md:col-span-2 hover:bg-green-500/10 transition-all duration-500 ease-out xl:order-none',
    content: (
      <>
        <Safari
          src="/landing/solution/4-light.png"
          url={config.site.url}
          className={cn(
            '-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]',
            'dark:hidden',
          )}
        />
        <Safari
          src="/landing/solution/4-dark.png"
          url={config.site.url}
          className={cn(
            '-mb-32 mt-4 max-h-64 w-full select-none px-4 drop-shadow-[0_0_28px_rgba(0,0,0,.1)] transition-all duration-300 group-hover:translate-y-[-10px]',
            'hidden dark:block',
          )}
        />
      </>
    ),
  },
]

export default function Solution() {
  return (
    <Section
      title="Solution"
      subtitle="Elevate Your Backlink Strategy, From Start to Finish"
      description="Discover high-value prospects, analyze domain and URL strength, convert opportunities into quality backlinks, and monitor your growing link profile—all with precision and ease."
      className=""
    >
      <div className="mx-auto mt-16 grid max-w-sm grid-cols-1 gap-6 text-muted-foreground md:max-w-3xl md:grid-cols-2 md:grid-rows-3 xl:max-w-6xl xl:auto-rows-fr xl:grid-cols-3 xl:grid-rows-2">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            className={cn(
              'group relative items-start overflow-hidden rounded-2xl border bg-card p-4',
              feature.className,
            )}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              type: 'spring',
              stiffness: 100,
              damping: 30,
              delay: index * 0.1,
            }}
            viewport={{ once: true }}
          >
            <div>
              <h3 className="mb-2 font-semibold text-primary">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
            {feature.content}
            <div className="pointer-events-none absolute bottom-0 left-0 h-32 w-full bg-gradient-to-t from-neutral-50 dark:from-neutral-900" />
          </motion.div>
        ))}
      </div>
    </Section>
  )
}
